import type React from 'react';
import { useEffect } from 'react';

import { Spacer } from '@zillow/constellation';
import { StackedChildren } from '@zillow/zrs-comet';

import { SECURITY_DEPOSIT } from '../../../constants/index';
import { FEE_PAYMENT_TIME, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import type { Section } from '../../../models/index';
import FeePaymentTime from '../../../components/Widgets/FeePaymentTime';
import SecurityDepositYesAndNoExpansion from './SecurityDeposit/SecurityDepositYesAndNoExpansion';
import SecurityDepositCurrencyInput from './SecurityDeposit/SecurityDepositCurrencyInput';
import SecurityDepositBankNameInput from './SecurityDeposit/SecurityDepositBankNameInput';
import SecurityDepositBankAddressInput from './SecurityDeposit/SecurityDepositBankAddressInput';
import SecurityDepositCleaningCostYesAndNo from './SecurityDeposit/SecurityDepositCleaningCostYesAndNo';
import SecurityDepositInterestYesAndNo from './SecurityDeposit/SecurityDepositInterestYesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function SecurityDeposit_v5({ section, setSectionValues }: Props): JSX.Element {
  useEffect(() => {
    section.setCustomFieldValidator(SECURITY_DEPOSIT.FIELD_NAME.AMOUNT, isAmountValid);
  }, []);

  function isAmountValid(amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > SECURITY_DEPOSIT.BOUNDS.MAX_AMOUNT) {
      section
        .getField(SECURITY_DEPOSIT.FIELD_NAME.AMOUNT)
        .setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(SECURITY_DEPOSIT.BOUNDS.MAX_AMOUNT));
      return false;
    }
    return true;
  }

  function disableSecurityDeposit(): void {
    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED]: false });
  }

  function enableSecurityDeposit(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED]: true,
      [SECURITY_DEPOSIT.FIELD_NAME.AMOUNT]: '',
    });
  }

  function onAmountChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.AMOUNT]: value });
  }

  function onBankNameChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.BANK_NAME]: value });
  }

  function onBankAddressChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.BANK_ADDRESS]: value });
  }

  function onPayDueClick(event: any): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_SIGN,
      [SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_LEASE_START,
    });
  }

  function onCleaningCostYesClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST]: true,
    });
  }

  function onCleaningCostNoClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST]: false,
    });
  }

  function onInterestYesClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.INTEREST]: true,
    });
  }

  function onInterestNoClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.INTEREST]: false,
    });
  }

  const cleaningCostFieldValue = section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST);
  const interestFieldValue = section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.INTEREST);

  return (
    <SecurityDepositYesAndNoExpansion
      yesHandler={enableSecurityDeposit}
      noHandler={disableSecurityDeposit}
      yesActive={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED)}
    >
      <StackedChildren size="sm">
        <Spacer marginTop="sm" />
        <SecurityDepositCurrencyInput onBlur={onAmountChange} onChange={onAmountChange} section={section} />
        <SecurityDepositBankNameInput
          value={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.BANK_NAME)}
          onChange={onBankNameChange}
        />
        <SecurityDepositBankAddressInput
          value={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.BANK_ADDRESS)}
          onChange={onBankAddressChange}
          section={section}
        />
        <FeePaymentTime
          label="When is the security deposit due?"
          section={section}
          onChange={onPayDueClick}
          firstChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING)}
          secondChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE)}
          firstField={SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING}
          secondField={SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE}
        />
        <SecurityDepositCleaningCostYesAndNo
          yesHandler={onCleaningCostYesClick}
          noHandler={onCleaningCostNoClick}
          yesActive={cleaningCostFieldValue}
          disabled={section.isFieldDisabled(SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST)}
        />
        <SecurityDepositInterestYesAndNo
          yesHandler={onInterestYesClick}
          noHandler={onInterestNoClick}
          yesActive={interestFieldValue}
          disabled={section.isFieldDisabled(SECURITY_DEPOSIT.FIELD_NAME.INTEREST)}
        />
      </StackedChildren>
    </SecurityDepositYesAndNoExpansion>
  );
}

SecurityDeposit_v5.description =
  'Asks for security deposit, when the deposit is due, bank name, bank address, yes/no for cleaning cost, and yes/no for interest';
